import React from 'react';
import { Button, Section, SectionHeader } from '../../design-system';
import MarketingLayout from '../layouts/MarketingLayout';
import FadeIn from '../components/FadeIn';
import { PRICING_TIERS } from '../data/content';
import SeoHead from '../seo/SeoHead';
import { PRICING_SEO } from '../seo/pages';

export default function Pricing() {
  return (
    <MarketingLayout>
      <SeoHead title={PRICING_SEO.title} description={PRICING_SEO.description} path={PRICING_SEO.path} />
      <section className="bg-invert-navy text-white pt-32 lg:pt-40 pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-widest text-invert-orange mb-6">
              Pricing
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Simple pricing. Serious results.
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Start free. Scale as you grow. Plus earn passive income from unused screen time.
            </p>
          </FadeIn>
        </div>
      </section>

      <Section>
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {PRICING_TIERS.map((tier, i) => (
            <FadeIn key={tier.name} delay={i * 0.1}>
              <div
                className={`rounded-2xl p-8 h-full flex flex-col ${
                  tier.highlighted
                    ? 'bg-invert-navy text-white ring-2 ring-invert-orange shadow-xl scale-105'
                    : 'bg-white border border-invert-border'
                }`}
              >
                <h3 className={`text-lg font-semibold mb-2 ${tier.highlighted ? 'text-white' : 'text-invert-navy'}`}>
                  {tier.name}
                </h3>
                <div className="mb-4">
                  <span className={`text-4xl font-bold ${tier.highlighted ? 'text-white' : 'text-invert-navy'}`}>
                    {tier.price}
                  </span>
                  {tier.period && (
                    <span className={tier.highlighted ? 'text-slate-300' : 'text-slate-500'}>
                      {tier.period}
                    </span>
                  )}
                </div>
                <p className={`text-sm mb-6 ${tier.highlighted ? 'text-slate-300' : 'text-slate-500'}`}>
                  {tier.description}
                </p>
                <ul className="space-y-3 mb-8 flex-1">
                  {tier.features.map((f) => (
                    <li
                      key={f}
                      className={`text-sm flex items-start gap-2 ${
                        tier.highlighted ? 'text-slate-200' : 'text-slate-600'
                      }`}
                    >
                      <span className="text-invert-orange mt-0.5">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  to={tier.name === 'Enterprise' ? '/contactUs' : '/screenHostSignup'}
                  variant={tier.highlighted ? 'primary' : 'outline'}
                  size="md"
                  className="w-full"
                >
                  {tier.cta}
                </Button>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section className="bg-slate-50">
        <SectionHeader
          label="Bonus Revenue"
          title="Advertising pays you back"
          subtitle="Advertising revenue isn't a price tier — it's a bonus. When your screens have unused time, INVERT fills them with local ads and shares the revenue with you."
        />
        <FadeIn className="max-w-2xl mx-auto text-center">
          <div className="glass-card-light rounded-2xl p-8">
            <p className="text-3xl font-bold text-invert-orange mb-2">Passive Income</p>
            <p className="text-slate-600">
              Your screens earn money even when you&apos;re not running promotions. Local advertisers reach your customers during natural content breaks.
            </p>
            <Button to="/advertise" variant="outline" className="mt-6">
              Learn About Advertising
            </Button>
          </div>
        </FadeIn>
      </Section>
    </MarketingLayout>
  );
}
